'use client';

import React from 'react';
import Link from 'next/link';
import { ExternalLink, Star } from 'lucide-react';
import { trackAffiliateClick } from '../../utils/analytics';
import { isInternalLink, extractInternalPath } from '../../utils/linkUtils';
import { AFFILIATE_REL, isAffiliateHref } from '../../lib/affiliateLinks';

interface AffiliateButtonProps {
  href: string;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  badge?: string;
  className?: string;
  isHeroPrimary?: boolean;
  // Disable visual motion/glow on mobile devices
  noMobilePulse?: boolean;
  noMobileGlow?: boolean;
  openInNewTab?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

const AffiliateButton: React.FC<AffiliateButtonProps> = ({
  href,
  children,
  variant = 'primary',
  size = 'md',
  showIcon = true,
  badge,
  className = '',
  isHeroPrimary = false,
  noMobilePulse = false,
  noMobileGlow = false,
  openInNewTab = false,
  onClick
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-semibold transition-all duration-300 rounded-xl relative overflow-hidden';
  const wrapperClasses = isHeroPrimary
    ? 'relative inline-block w-full sm:w-auto'
    : `relative inline-block ${className.includes('w-full') ? 'w-full' : ''}`;
  const heroWidthClasses = isHeroPrimary ? 'w-full sm:w-auto' : '';
  
  const variantClasses = {
    primary: 'glass-button',
    secondary: 'bg-charcoal-900 border border-pink-400/30 text-pink-300 hover:bg-charcoal-800 transition-colors duration-300',
    outline: 'border border-pink-400/30 text-pink-300 backdrop-blur-sm hover:bg-pink-500/10 hover:border-pink-300/50'
  };

  const sizeClasses = {
    // Slightly smaller text on mobile to help keep single-line CTAs
    sm: 'px-4 py-2 text-xs sm:text-sm gap-1',
    md: 'px-6 py-3 text-sm sm:text-base gap-2',
    lg: 'px-8 py-3.5 text-base sm:text-lg gap-3'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const handleClick = () => {
    setTimeout(() => {
      let platform = 'unknown';
      if (typeof href === 'string' && (href.includes('candy') || href.includes('salesmrr'))) {
        platform = 'Candy AI';
      } else if (typeof href === 'string' && href.includes('dream')) {
        platform = 'DreamGF';
      } else if (typeof href === 'string' && href.includes('soul')) {
        platform = 'SoulGen';
      }
      trackAffiliateClick(href, platform);
    }, 0);
  };

  // Determine if link is internal:
  // - Links starting with / (e.g., /blog, /heavenpedia)
  // - Links to heavengirlfriend.com (e.g., https://heavengirlfriend.com/blog)
  const isInternal = typeof href === 'string' && isInternalLink(href);
  const isAffiliate = typeof href === 'string' && isAffiliateHref(href);
  const shouldOpenInNewTab = isAffiliate || (!isInternal && openInNewTab);

  // Extract path from full URL if it's a heavengirlfriend.com link
  const linkPath = isInternal ? extractInternalPath(href) : href;

  return (
    <div className={wrapperClasses} data-hero-primary={isHeroPrimary || undefined}>
      {isInternal ? (
        <Link
          href={linkPath}
          target={shouldOpenInNewTab ? '_blank' : undefined}
          rel={shouldOpenInNewTab ? (isAffiliate ? AFFILIATE_REL : 'noopener noreferrer') : undefined}
          className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${heroWidthClasses} ${className} group overflow-visible ${noMobileGlow ? 'shadow-none md:shadow' : ''}`}
          onClick={onClick}
        >
          {/* Container for shimmer effect with controlled overflow */}
          <div className="absolute inset-0 overflow-hidden rounded-xl">
            <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out transform -skew-x-12 z-0 ${noMobileGlow ? 'hidden md:block' : ''}`}></div>
          </div>
          
          {/* Button content with z-index to appear above the shimmer and gradient overlays */}
          <span className="relative z-20 flex items-center gap-2 whitespace-nowrap">
            {children}
            {showIcon && (
              <ExternalLink className={iconSizes[size]} />
            )}
          </span>

          {/* Badge positioned relative to the button itself */}
          {badge && (
            <div className={`absolute -top-3 -right-3 md:-top-2 md:-right-2 bg-gradient-to-r from-yellow-500 to-amber-500 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 z-30 shadow-lg ${noMobilePulse ? 'md:animate-pulse-soft' : 'animate-pulse-soft'}`}>
              <Star className="w-3 h-3 fill-yellow-900" />{badge}
            </div>
          )}
        </Link>
      ) : (
        <a
          href={href}
          target="_blank"
          rel={isAffiliate ? AFFILIATE_REL : 'noopener noreferrer'}
          className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${heroWidthClasses} ${className} group overflow-visible ${noMobileGlow ? 'shadow-none md:shadow' : ''}`}
          onClick={handleClick}
        >
          {/* Container for shimmer effect with controlled overflow */}
          <div className="absolute inset-0 overflow-hidden rounded-xl">
            <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out transform -skew-x-12 z-0 ${noMobileGlow ? 'hidden md:block' : ''}`}></div>
          </div>
          
          {/* Button content with z-index to appear above the shimmer and gradient overlays */}
          <span className="relative z-20 flex items-center gap-2 whitespace-nowrap">
            {children}
            {showIcon && (
              <ExternalLink className={iconSizes[size]} />
            )}
          </span>

          {/* Badge positioned relative to the button itself */}
          {badge && (
            <div className={`absolute -top-3 -right-3 md:-top-2 md:-right-2 bg-gradient-to-r from-yellow-500 to-amber-500 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 z-30 shadow-lg ${noMobilePulse ? 'md:animate-pulse-soft' : 'animate-pulse-soft'}`}>
              <Star className="w-3 h-3 fill-yellow-900" />{badge}
            </div>
          )}
        </a>
      )}
    </div>
  );
};

export default AffiliateButton;
